import { useState, useMemo, useEffect } from 'react'
import {
  Modal,
  Input,
  Button,
  Typography,
  Flex,
  Space,
  Radio,
  Alert,
} from 'antd'
import { CloseCircleOutlined, SendOutlined } from '@ant-design/icons'
import { useStore } from 'effector-react'

import { submitReject, rejectVerification } from './model'
import { verificationRejectReason, verificationStatus } from '../../const'

const { Text } = Typography

type Props = {
  requestId: string
  isVisible: boolean
  onClose: () => void
}

type ReasonDef = {
  id: string
  key: string
  label: string
  isOther: boolean
}

const REASONS: ReasonDef[] = Object.entries(verificationRejectReason).map(
  ([name, config]) => ({
    id: name,
    key: config.key,
    label: config.label,
    isOther: name === 'otherReason',
  }),
)

function ReasonCard({
  reason,
  selected,
  onSelect,
  otherText,
  onOtherChange,
}: {
  reason: ReasonDef
  selected: string | null
  onSelect: (id: string) => void
  otherText: string
  onOtherChange: (val: string) => void
}) {
  const isSelected = selected === reason.id
  const border = isSelected ? '1.5px solid var(--crm-accent-blue, #3b82f6)' : '1px solid var(--crm-border)'
  const bg = isSelected ? 'var(--crm-bg-elevated)' : 'var(--crm-bg)'

  return (
    <div
      onClick={() => onSelect(reason.id)}
      style={{
        border,
        borderRadius: 8,
        padding: '12px 14px',
        background: bg,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      <Flex align="flex-start" gap={10}>
        <Radio
          checked={isSelected}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ fontSize: 13 }}>
            {reason.label}
          </Text>

          {isSelected && reason.isOther && (
            <div style={{ marginTop: 10 }}>
              <Text
                type="secondary"
                style={{
                  fontSize: 11,
                  display: 'block',
                  marginBottom: 3,
                }}
              >
                Enter a reason
              </Text>
              <Input.TextArea
                rows={3}
                placeholder="Enter custom rejection reason..."
                value={otherText}
                onChange={(e) => onOtherChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
                style={{ fontFamily: 'monospace' }}
              />
            </div>
          )}

          {isSelected && !reason.isOther && (
            <div
              style={{
                marginTop: 10,
                background: 'var(--crm-bg-elevated)',
                border: '1px solid var(--crm-border)',
                borderRadius: 6,
                padding: '8px 10px',
              }}
            >
              <Flex
                align="center"
                gap={6}
                style={{ marginBottom: 4 }}
              >
                <SendOutlined
                  style={{ color: 'var(--crm-accent-blue, #3b82f6)', fontSize: 11 }}
                />
                <Text
                  type="secondary"
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Message to client
                </Text>
              </Flex>
              <Text style={{ fontSize: 12, fontStyle: 'italic' }}>
                "{reason.label}"
              </Text>
            </div>
          )}
        </div>
      </Flex>
    </div>
  )
}

export const RejectModal = ({ requestId, isVisible, onClose }: Props) => {
  const isLoading = useStore(rejectVerification.pending)
  const [selected, setSelected] = useState<string | null>(null)
  const [otherText, setOtherText] = useState('')
  const [commentText, setCommentText] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (isVisible) {
      setSelected(null)
      setOtherText('')
      setCommentText('')
      setError('')
    }
  }, [isVisible])

  const handleSelect = (id: string) => {
    setSelected(id)
    setOtherText('')
    setError('')
  }

  const selectedReason = useMemo(
    () => REASONS.find((r) => r.id === selected),
    [selected],
  )

  const canSubmit = useMemo(() => {
    if (!selectedReason) return false
    if (selectedReason.isOther && !otherText.trim()) return false
    return true
  }, [selectedReason, otherText])

  const handleSubmit = () => {
    if (!selectedReason) {
      setError('Please select a reason')
      return
    }
    if (selectedReason.isOther && !otherText.trim()) {
      setError('Please enter a reason')
      return
    }

    submitReject({
      requestId,
      status: verificationStatus.verificationRequestStatusRejected(),
      reason: selectedReason.isOther ? otherText : selectedReason.key,
      comment: commentText,
    })
    onClose()
  }

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      title={
        <Flex align="center" gap={8}>
          <CloseCircleOutlined style={{ color: 'var(--crm-accent-red, #ef4444)' }} />
          <span>Set reject reason and comment</span>
        </Flex>
      }
      width={900}
      footer={
        <Flex justify="space-between" align="center">
          <Text type="secondary" style={{ fontSize: 11 }}>
            {selected
              ? 'Review the message preview before sending'
              : 'Select a reason to continue'}
          </Text>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              danger
              disabled={!canSubmit || isLoading}
              loading={isLoading}
              icon={<SendOutlined />}
              onClick={handleSubmit}
            >
              Reject
            </Button>
          </Space>
        </Flex>
      }
      styles={{
        body: {
          padding: '16px 24px',
          maxHeight: '70vh',
          overflowY: 'auto',
        },
        header: { paddingBottom: 12 },
      }}
    >
      {error && (
        <Alert
          message={error}
          type="error"
          style={{ marginBottom: 12 }}
        />
      )}

      <Text
        type="secondary"
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          display: 'block',
          marginBottom: 10,
        }}
      >
        Rejection reason
      </Text>

      <Flex vertical gap={8}>
        {REASONS.map((reason) => (
          <ReasonCard
            key={reason.id}
            reason={reason}
            selected={selected}
            onSelect={handleSelect}
            otherText={otherText}
            onOtherChange={setOtherText}
          />
        ))}
      </Flex>

      <Text
        type="secondary"
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          display: 'block',
          marginTop: 16,
          marginBottom: 10,
        }}
      >
        Comment
      </Text>

      <Input.TextArea
        rows={3}
        placeholder="Enter a comment..."
        value={commentText}
        onChange={(e) => setCommentText(e.target.value)}
        style={{ fontFamily: 'monospace' }}
      />
    </Modal>
  )
}
